import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CalendarClock, Send } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useSocialSchedulerApi } from '../../../common/hooks/use-social-scheduler-api.hook';
import { useContentStudioApi } from '../../../common/hooks/use-content-studio-api.hook';
import { SocialAccount, SocialPost } from '../../../common/models/social-scheduler.model';
import { PostCard } from '../social-scheduler/components/PostCard';

/**
 * What the scheduler is about to do, on the dashboard.
 *
 * A scheduled post is the one thing in the product that acts on its own, at a
 * time nobody is watching. The calendar shows it if you go and look; this is
 * for the vendor who does not — which is the point of a dashboard.
 *
 * Three numbers and the next few posts. Deliberately not the calendar in
 * miniature: a month grid at widget size is unreadable, and the question here
 * is "is anything about to go out, and did anything break", not "what does
 * October look like".
 */

/** Enough to answer "what is next" without becoming the Posts page. */
const UP_NEXT = 3;

interface Counts {
    scheduled: number;
    published: number;
    failed: number;
}

export const SocialPostsCard: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const api = useSocialSchedulerApi();
    const studio = useContentStudioApi();

    const [counts, setCounts] = useState<Counts | null>(null);
    const [next, setNext] = useState<SocialPost[]>([]);
    /** uuid to image url, for the posts on show only. */
    const [thumbs, setThumbs] = useState<Record<string, string>>({});
    const [kinds, setKinds] = useState<Record<string, 'image' | 'video' | 'upload'>>({});
    /** For the preview's header — whose account this goes out as. */
    const [accounts, setAccounts] = useState<SocialAccount[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                /*
                 * Counted from the totals rather than by fetching every post:
                 * the list endpoint reports `total` for the filter, so three
                 * small requests answer the numbers no matter how many posts
                 * a vendor has.
                 */
                const [scheduled, published, failed, connected] = await Promise.all([
                    api.listPosts({ status: 'scheduled', limit: UP_NEXT }),
                    api.listPosts({ status: 'published', limit: 1 }),
                    api.listPosts({ status: 'failed', limit: 1 }),
                    api.listAccounts({ limit: 20 }).then((r) => r.items || []).catch(() => []),
                ]);

                if (cancelled) return;
                setAccounts(connected);
                setCounts({
                    scheduled: scheduled.total ?? 0,
                    published: published.total ?? 0,
                    failed: failed.total ?? 0,
                });
                // Soonest first: the list comes back newest-first, which for
                // things that have not happened yet is the wrong end.
                const upcoming = [...(scheduled.items || [])].sort(
                    (a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime(),
                );
                setNext(upcoming);

                /*
                 * The picture, for the posts actually on show.
                 *
                 * The scheduler page builds a uuid-to-url map by pulling 500
                 * assets, which is right for a page rendering a month and
                 * wasteful for three rows. These are fetched one by one and
                 * only for what is rendered.
                 */
                const wanted = upcoming
                    .map((post) => post.assetUuids?.[0] || post.assetUuid)
                    .filter(Boolean) as string[];

                const found = await Promise.all(
                    wanted.map((uuid) =>
                        studio.getAsset(uuid)
                            .then((a: any) => ({ uuid, url: a?.url as string, kind: a?.kind }))
                            .catch(() => null),
                    ),
                );
                if (cancelled) return;

                const urls: Record<string, string> = {};
                const byKind: Record<string, 'image' | 'video' | 'upload'> = {};
                for (const asset of found) {
                    if (!asset?.url) continue;
                    urls[asset.uuid] = asset.url;
                    byKind[asset.uuid] = asset.kind === 'video' ? 'video' : 'image';
                }
                setThumbs(urls);
                setKinds(byKind);
            } catch {
                // A dashboard widget that cannot load is a quiet empty state,
                // not an error over the whole page.
                if (!cancelled) setCounts({ scheduled: 0, published: 0, failed: 0 });
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const stat = (label: string, value: number, tone?: string) => (
        <div className="flex flex-col gap-0.5">
            <span className={cn('text-xl font-medium tabular-nums', tone)}>{value}</span>
            <span className="text-xs text-muted-foreground">{label}</span>
        </div>
    );

    return (
        <div className="flex h-full flex-col gap-3 rounded-xl border bg-card p-4">
            <div className="flex items-center gap-2">
                <CalendarClock className="size-4 text-muted-foreground" />
                <h3 className="text-sm font-medium">
                    {t('PAGES.DASHBOARD.SOCIAL_POSTS', 'Scheduled posts')}
                </h3>
            </div>

            {loading ? (
                <div className="flex flex-col gap-2">
                    <Skeleton className="h-8 w-40" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-2/3" />
                </div>
            ) : (
                <>
                    <div className="flex items-start gap-6">
                        {stat(t('SOCIAL_SCHEDULER.STATUS.SCHEDULED', 'Scheduled') as string, counts?.scheduled ?? 0)}
                        {stat(t('SOCIAL_SCHEDULER.STATUS.PUBLISHED', 'Published') as string, counts?.published ?? 0)}
                        {/* Only coloured when there is something wrong: a red
                            zero trains people to ignore the colour. */}
                        {stat(
                            t('SOCIAL_SCHEDULER.STATUS.FAILED', 'Failed') as string,
                            counts?.failed ?? 0,
                            counts?.failed ? 'text-destructive' : undefined,
                        )}
                    </div>

                    {!!counts?.failed && (
                        <button
                            type="button"
                            onClick={() => navigate('/marketing/social-scheduler?tab=posts')}
                            className="flex items-center gap-1.5 rounded-lg border border-destructive/40 px-2.5 py-1.5 text-start text-xs text-destructive transition-colors hover:bg-destructive/5"
                        >
                            <AlertTriangle className="size-3.5 shrink-0" />
                            {t('PAGES.DASHBOARD.SOCIAL_FAILED', 'Something did not go out. Take a look.')}
                        </button>
                    )}

                    <div className="min-h-0 flex-1 overflow-y-auto">
                        {next.length === 0 ? (
                            <p className="text-xs text-muted-foreground">
                                {t('PAGES.DASHBOARD.SOCIAL_NONE', 'Nothing is scheduled.')}
                            </p>
                        ) : (
                            /*
                             * The scheduler's own preview, not an imitation of it.
                             *
                             * A row with a 40px thumbnail told a vendor a post
                             * existed; this shows the post — the account it goes out
                             * as, the picture at full width, the caption underneath.
                             * Reusing the component means the dashboard cannot drift
                             * away from the page it is summarising.
                             */
                            <div className="flex flex-col gap-3">
                                {next.map((post) => {
                                    const account = accounts.find((a) => a.uuid === post.accountUuid);
                                    return (
                                        <PostCard
                                            key={post.uuid}
                                            post={post}
                                            assetUrlMap={thumbs}
                                            assetKindMap={kinds}
                                            accountName={account?.accountName}
                                            accountAvatar={account?.accountAvatar}
                                            onClick={(uuid) => navigate(`/marketing/social-scheduler/post/${uuid}`)}
                                        />
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <Button
                        variant="outline"
                        size="sm"
                        className="self-start"
                        onClick={() => navigate('/marketing/social-scheduler')}
                    >
                        <Send data-icon="inline-start" />
                        {t('PAGES.DASHBOARD.SOCIAL_OPEN', 'Open scheduler')}
                    </Button>
                </>
            )}
        </div>
    );
};
